import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useGlobalSearch } from '@/hooks/useRootQueries'
import { LoadingSkeleton } from '@/components/States'
import { StatusBadge } from '@/components/StatusBadge'
import { DocumentDetailDrawer } from '@/components/DocumentDetailDrawer'
import { IconOrganizations, IconUsers, IconDocuments, IconSignatures } from '@/components/Icons'
import type { Organization, PlatformUser, DocumentItem, SignerItem } from '@/types/api'

export function SearchResults() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const q = params.get('q') ?? ''
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null)

  const search = useGlobalSearch(q)
  const results = search.data

  const organizations = Array.isArray(results?.organizations) ? results.organizations : []
  const users = Array.isArray(results?.users) ? results.users : []
  const documents = Array.isArray(results?.documents) ? results.documents : []
  const signers = Array.isArray(results?.signers) ? results.signers : []

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900 tracking-tight">
            Search results for <span className="text-sky-600 font-mono">"{q}"</span>
          </h1>
          <p className="mt-0.5 text-xs text-slate-500 font-medium">
            Cross-cluster query results across organizations, users, documents, and signatures.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        {/* Organizations */}
        <ResultGroup title="Organizations" count={organizations.length} loading={search.isLoading}>
          {organizations.map((o: Organization) => (
            <button
              key={o.id}
              onClick={() => navigate(`/organizations/${o.id}`)}
              className="flex items-center justify-between w-full rounded-xl border border-transparent p-2.5 text-left text-xs hover:border-sky-200 hover:bg-sky-50/40 transition-all"
            >
              <div className="flex items-center gap-2.5">
                <IconOrganizations className="h-4 w-4 text-slate-500" />
                <div>
                  <div className="text-slate-900 font-bold">{o.name}</div>
                  <div className="font-mono text-[10.5px] text-slate-500">{o.slug}</div>
                </div>
              </div>
              <StatusBadge status={o.status} />
            </button>
          ))}
          {!search.isLoading && organizations.length === 0 && (
            <p className="px-3 py-4 text-xs text-slate-400 text-center font-mono">No matching organizations found.</p>
          )}
        </ResultGroup>

        {/* Users */}
        <ResultGroup title="Platform Users" count={users.length} loading={search.isLoading}>
          {users.map((u: PlatformUser) => (
            <button
              key={u.id}
              onClick={() => navigate(`/users/${u.id}`)}
              className="flex items-center justify-between w-full rounded-xl border border-transparent p-2.5 text-left text-xs hover:border-sky-200 hover:bg-sky-50/40 transition-all"
            >
              <div className="flex items-center gap-2.5">
                <IconUsers className="h-4 w-4 text-slate-500" />
                <div>
                  <div className="text-slate-900 font-bold">{u.name}</div>
                  <div className="font-mono text-[10.5px] text-slate-500">{u.email}</div>
                </div>
              </div>
              <StatusBadge status={u.status} />
            </button>
          ))}
          {!search.isLoading && users.length === 0 && (
            <p className="px-3 py-4 text-xs text-slate-400 text-center font-mono">No matching users found.</p>
          )}
        </ResultGroup>

        {/* Documents */}
        <ResultGroup title="Documents" count={documents.length} loading={search.isLoading}>
          {documents.map((d: DocumentItem) => (
            <button
              key={d.id}
              onClick={() => setSelectedDocId(d.id)}
              className="flex items-center justify-between w-full rounded-xl border border-transparent p-2.5 text-left text-xs hover:border-sky-200 hover:bg-sky-50/40 transition-all"
            >
              <div className="flex items-center gap-2.5">
                <IconDocuments className="h-4 w-4 text-slate-500" />
                <div>
                  <div className="text-slate-900 font-bold">{d.title}</div>
                  <div className="font-mono text-[10.5px] text-slate-500">{d.category || 'General'} · ID: {d.id.slice(-6)}</div>
                </div>
              </div>
              <StatusBadge status={d.status} />
            </button>
          ))}
          {!search.isLoading && documents.length === 0 && (
            <p className="px-3 py-4 text-xs text-slate-400 text-center font-mono">No matching documents found.</p>
          )}
        </ResultGroup>

        {/* Signers / Signatures */}
        <ResultGroup title="Signers & Signatures" count={signers.length} loading={search.isLoading}>
          {signers.map((s: SignerItem) => (
            <button
              key={s.id}
              onClick={() => setSelectedDocId(s.docId)}
              className="flex items-center justify-between w-full rounded-xl border border-transparent p-2.5 text-left text-xs hover:border-sky-200 hover:bg-sky-50/40 transition-all"
            >
              <div className="flex items-center gap-2.5">
                <IconSignatures className="h-4 w-4 text-slate-500" />
                <div>
                  <div className="text-slate-900 font-bold">{s.name}</div>
                  <div className="font-mono text-[10.5px] text-slate-500">{s.email}</div>
                </div>
              </div>
              <StatusBadge status={s.status} />
            </button>
          ))}
          {!search.isLoading && signers.length === 0 && (
            <p className="px-3 py-4 text-xs text-slate-400 text-center font-mono">No matching signers found.</p>
          )}
        </ResultGroup>
      </div>

      {/* Document Detail Drawer */}
      <DocumentDetailDrawer
        open={!!selectedDocId}
        onClose={() => setSelectedDocId(null)}
        documentId={selectedDocId}
      />
    </div>
  )
}

function ResultGroup({
  title,
  count,
  loading,
  children,
}: {
  title: string
  count?: number
  loading: boolean
  children: React.ReactNode
}) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-xs overflow-hidden">
      <div className="border-b border-slate-200 bg-slate-50/80 px-4 py-3 flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-wider text-slate-700 font-bold">
          {title}
        </span>
        {count !== undefined && (
          <span className="font-mono text-[10.5px] font-bold text-slate-500 bg-slate-200/60 px-2 py-0.5 rounded-full">
            {count}
          </span>
        )}
      </div>
      <div className="p-2 space-y-1">{loading ? <LoadingSkeleton rows={3} /> : children}</div>
    </div>
  )
}
